/**
 * Rules — one plan's funder interface as it stands: every active rule, what
 * kind it is, when it applies, and the source sentence behind it. A person can
 * correct a value (that becomes a proposal to approve, which supersedes the old
 * rule from its effective date — history is kept) or expire a rule with a
 * reason. The harness's Fix lands here with the rules a finding involves
 * marked; a change made from there closes the finding once it is approved.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarX, ExternalLink, Pencil, RefreshCw, Search } from 'lucide-react';
import { funderAgentsApi, type FunderPlan, type FunderRule, type RuleClass } from '../../../shared/services/funderAgentsApi';
import { Btn, Empty, ErrorLine, NoteForm, ValueEditor, errText, fmtDay, showValue, type Notify } from './shared';

const CLASS_LABEL: Record<RuleClass, string> = {
  generic: 'Generic',
  statewide: 'State',
  plan_specific: 'Its own',
};

/** The rules a harness finding involves, so the correction is linked to it. */
export interface RulesFocus {
  findingId: number;
  ruleIds: number[];
  message: string;
}

export function RuleRow({
  rule,
  notify,
  onChanged,
  onProposed,
  findingId,
}: {
  rule: FunderRule;
  notify: Notify;
  onChanged: () => void;
  onProposed?: () => void;
  findingId?: number;
}) {
  const [mode, setMode] = useState<'edit' | 'expire' | null>(null);
  return (
    <li className={`p-3 space-y-1.5 ${findingId ? 'bg-attention-soft' : ''}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-paper-2 text-ink-700">{rule.program_type}</span>
        <span
          className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
            rule.class === 'plan_specific' ? 'bg-v2accent-soft text-v2accent-ink' : 'bg-paper-2 text-ink-500'
          }`}
        >
          {CLASS_LABEL[rule.class] ?? rule.class}
        </span>
        <code className="font-semibold text-ink">{rule.rule_key}</code>
        <span className="text-ink-500">=</span>
        <code className="rounded bg-paper-2 px-1.5 py-0.5 text-ink break-all">{showValue(rule.rule_value)}</code>
        <span className="text-xs text-ink-500">
          from {fmtDay(rule.effective_date)}
          {rule.expiration_date ? ` to ${fmtDay(rule.expiration_date)}` : ''}
        </span>
        <span className="ml-auto flex gap-1">
          <Btn tone="ghost" onClick={() => setMode(mode === 'edit' ? null : 'edit')}>
            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
            Edit
          </Btn>
          <Btn tone="ghost" onClick={() => setMode(mode === 'expire' ? null : 'expire')}>
            <CalendarX className="w-3.5 h-3.5" aria-hidden="true" />
            Expire
          </Btn>
        </span>
      </div>
      {rule.excerpt && <blockquote className="border-l-2 border-v2accent pl-3 text-xs text-ink-700 italic">“{rule.excerpt}”</blockquote>}
      {rule.source_url ? (
        <a href={rule.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-v2accent-ink hover:underline">
          Source <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </a>
      ) : (
        rule.class === 'plan_specific' && <p className="text-xs text-attention-ink">No source on record for this rule.</p>
      )}
      {mode === 'edit' && (
        <ValueEditor
          initial={rule.rule_value}
          submitLabel="Propose this change"
          onCancel={() => setMode(null)}
          onSubmit={async (value, note) => {
            try {
              const res = await funderAgentsApi.editRule(rule.id, { rule_value: value, note, finding_id: findingId });
              setMode(null);
              notify(
                true,
                `Sent for approval${res?.proposal_id ? ` (proposal #${res.proposal_id})` : ''}: ${rule.rule_key}` +
                  `${res?.effective_date ? ` from ${fmtDay(res.effective_date)}` : ''}. It applies once approved in Review.`,
              );
              onChanged();
              onProposed?.();
            } catch (e) {
              notify(false, errText(e));
            }
          }}
        />
      )}
      {mode === 'expire' && (
        <NoteForm
          label="Why expire this rule? It stops applying today; history is kept."
          submitLabel="Expire rule"
          onCancel={() => setMode(null)}
          onSubmit={async (note) => {
            try {
              await funderAgentsApi.expireRule(rule.id, { note });
              setMode(null);
              notify(true, `${rule.rule_key} expired.`);
              onChanged();
            } catch (e) {
              notify(false, errText(e));
            }
          }}
        />
      )}
    </li>
  );
}

export default function RulesView({
  notify,
  plan,
  onPickPlan,
  focus,
  onProposed,
}: {
  notify: Notify;
  plan: FunderPlan | null;
  onPickPlan: (p: FunderPlan) => void;
  focus?: RulesFocus | null;
  onProposed?: () => void;
}) {
  const [rules, setRules] = useState<FunderRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<FunderPlan[]>([]);

  const load = useCallback(async () => {
    if (!plan) return;
    setError(null);
    try {
      setRules(await funderAgentsApi.rules(plan.payer_id));
    } catch (e) {
      setError(errText(e));
      setRules([]);
    }
  }, [plan]);

  useEffect(() => {
    setRules(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!q.trim()) {
      setMatches([]);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        setMatches((await funderAgentsApi.plans({ q: q.trim(), limit: 8 })).plans);
      } catch {
        setMatches([]);
      }
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <label className="text-xs font-semibold text-ink-700">
          Plan
          <span className="mt-1 flex items-center gap-1 rounded-md border border-v2border bg-surface px-2">
            <Search className="w-3.5 h-3.5 text-ink-400" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={plan ? plan.payer_name : 'Search a plan'} className="w-full bg-transparent py-1.5 text-sm text-ink outline-none" />
          </span>
        </label>
        {matches.length > 0 && (
          <ul className="absolute z-10 mt-1 w-full rounded-md border border-v2border bg-surface shadow-v2-2">
            {matches.map((m) => (
              <li key={m.payer_id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-ink hover:bg-paper-2"
                  onClick={() => {
                    onPickPlan(m);
                    setQ('');
                    setMatches([]);
                  }}
                >
                  {m.payer_name} <span className="text-xs text-ink-500">{(m.states ?? []).slice(0, 3).join(', ')}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {!plan ? (
        <Empty>Pick a plan to see its rules — here, or from the Rules button in Plans.</Empty>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-ink">
              {plan.payer_name}{' '}
              <span className="font-normal text-ink-500">
                {plan.rules ? `· ${plan.rules.generic} generic · ${plan.rules.statewide} state · ${plan.rules.plan_specific} its own` : ''}
              </span>
            </h4>
            <Btn tone="ghost" title="Refresh" onClick={() => void load()}>
              <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            </Btn>
          </div>
          <ErrorLine text={error} />
          {focus && <p className="rounded-md bg-attention-soft px-3 py-2 text-sm text-attention-ink">{focus.message}</p>}
          {rules === null ? (
            <p className="text-sm text-ink-500">Loading rules…</p>
          ) : rules.length === 0 ? (
            <Empty>No active rules. Add a source URL for this plan in Plans, then draft rules.</Empty>
          ) : (
            <ul className="divide-y divide-v2border rounded-md border border-v2border bg-surface">
              {rules.map((r) => (
                <RuleRow
                  key={r.id}
                  rule={r}
                  notify={notify}
                  onChanged={() => void load()}
                  onProposed={onProposed}
                  findingId={focus?.ruleIds.includes(r.id) ? focus.findingId : undefined}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
